import React, { useState, useEffect, useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import CommentContainer from 'components/Comment/CommentContainer';
import FeedLoadingView from 'components/feed/FeedLoadingView';
import { usePagination } from 'components/hooks/usePagination';
import Post from 'models/post';
import { useDispatch } from 'react-redux';
import AutomatedComment from 'screens/common/sidepanel/Comments/AutomatedComment';
import NoCommentsView from 'screens/common/sidepanel/Comments/NoCommentsView';
import SidepanelCommentDisplay from 'screens/common/sidepanel/Comments/SidepanelCommentDisplay';
import styled from 'styled-components';
import { StyledButton } from 'styles/common';

import { LoadingIcon } from 'constants/icons';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { getLevelUpCompetencyComments, updateLevelUpCompetencyComment } from 'services/growth/oneOnOne';
import { showErrorSnackbar } from 'services/snackbar';

import useLevelUpSidePanelContext from '../../context';

const CommentsList = ({
  employeeId,
  levelUpCompetencyId,
  competencyId,
  levelUpId,
  subCompetencyId,
  comments = [],
  setComments,
  forceReloadComments,
}) => {
  const { forceReloadOnCommentEdit } = useLevelUpSidePanelContext();
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { page, hasMore, updatePagination } = usePagination();

  const loadComments = () => {
    if (loading) return;
    setLoading(true);
    const limit = 10;
    const offset = page * limit;

    getLevelUpCompetencyComments({
      levelUpCompetencyId,
      limit,
      offset,
    })
      .then((comments) => {
        updatePagination({ hasMore: comments.length === limit, page: page + 1 });
        setComments((state) => [...comments.reverse(), ...state]);
      })
      .catch((err) => {
        dispatch(showErrorSnackbar('Sorry an error occured while fetching the task comments'));
      })
      .finally(() => setLoading(false));
  };

  const updateLocalAndFeedComment = useCallback(
    ({ commentId, comment }) => {
      setComments((comments) => comments.map((c) => (c.id === commentId ? { ...comment } : c)));
    },
    [dispatch, comments]
  );

  const handleCommentEdit = async ({
    commentId,
    commentEditorState,
    media,
    visibilityType,
    sentiment,
    isKudos = false,
  }) => {
    const res = await updateLevelUpCompetencyComment({
      commentId,
      employeeId,
      competencyId,
      levelUpId,
      subCompetencyId,
      commentEditorState,
      media,
      visibilityType,
      sentiment,
      isKudos,
    });
    queryClient.invalidateQueries([REACT_QUERY_KEYS.CompetencySidePanel]);
    // forceReloadOnCommentEdit();
    return res;
  };

  useEffect(() => {
    if (levelUpCompetencyId) {
      loadComments();
    }
    return () => {
      setComments([]);
    };
  }, [levelUpCompetencyId, forceReloadComments]);

  if (loading) {
    return <FeedLoadingView />;
  }

  return (
    <Container>
      {hasMore && (
        <StyledButton
          startIcon={<LoadingIcon color={loading ? 'textSecondary' : 'primary'} size="tiny" />}
          color="primary"
          bottomGutter={2}
          size="small"
          topGutter={-0.5}
          variant="text"
          onClick={loadComments}
          disabled={loading}
        >
          Load Previous Comments
        </StyledButton>
      )}
      {comments &&
        comments.map((comment) => (
          <CommentContainer
            handleCommentEdit={handleCommentEdit}
            placeholder="Share suggestion or feedback"
            key={`${comment.id}-taskComment`}
            {...comment}
            updateFeedComment={updateLocalAndFeedComment}
            feedId={levelUpCompetencyId}
            feedType="TASK"
            displayReply={false}
            employeeId={employeeId}
            renderDisplayCommentView={({ subType, ...props }) =>
              subType === Post.SUB_TYPES.automatedComment ? (
                <AutomatedComment {...props} />
              ) : (
                <SidepanelCommentDisplay {...props} enableKudos={false} />
              )
            }
            onlyEditingAllowed
            enableKudos={false}
          />
        ))}
      {comments && comments.length === 0 && <NoCommentsView />}
    </Container>
  );
};

const Container = styled.div`
  margin-bottom: 1em;
  .automatedComment + .automatedComment {
    margin-bottom: -0.5rem;
  }
`;

export default CommentsList;
